import { useCallback, useMemo } from 'react';
import { Layer } from 'shared/map-exports';
import {
  EvacZoneStatus,
  GeoEventEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { Poi, EvacZonePoi } from 'features/mapping/mapping.types';
import { Location } from 'features/locations/location.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { usePoisState } from 'state/usePoisState';
import { useMapLayersState } from 'state/useMapLayersState';
import { useGeoEventQuery } from 'features/geoEvents/hooks/useGeoEventQuery';
import useAppInfo from 'hooks/useAppInfo';
import { useActiveEvacZonesQuery } from 'features/evacZones/hooks/useActiveEvacZonesQuery';
import VisibilityToggledSource from '../VisibilityToggledSource';
import {
  VIEW_SOURCE_ID,
  FEATURE_UID_V2_FIELD_NAME,
  EVAC_ZONES_VIEW_FILL_LAYER_ID,
  EVAC_ZONES_SOURCE_LAYER_NAME,
  EVAC_ZONES_VIEW_STROKE_LAYER_ID,
  EVAC_ZONES_VIEW_PATTERN_LAYER_ID,
  EVAC_ZONES_VIEW_LABEL_LAYER_ID,
} from './constants';
import { getSelectedEvacZoneUId } from './utils';
import {
  getFillStyle,
  getLabelLayerStyle,
  getPatternStyle,
  getSelectedZonePatternStyle,
  getSelectedZoneStrokeStyle,
  getStrokeStyle,
} from './layerStyles';
import { EvacZoneStyles } from '../../../../constants';
import { BEFORE_LAYER_ID } from '../../styles/constants';
import { MapboxFeature, useMapLayerEvents } from '../useMapLayerEvents';
import { getWatchdutyTileUrl } from '../getWatchdutyTileUrl';

type EvacZonesProperties = {
  display_name: string;
  source_attribution: string;
  uid_v2: string;
  status?: EvacZoneStatus | '';
  evac_zone_style: RegionEvacZoneStyle;
  region_id: number;
  date_created: string;
  date_modified: string;
};

const REFRESH_INTERVAL_SECONDS = 30;

const handleSingleClickFeature = (
  feature: MapboxFeature<EvacZonesProperties>,
  setSelectedPoi: (poi: Poi) => void,
  activeEvacZones: GeoEventEvacZoneStatus[],
): void => {
  if (!feature || !feature.properties) return;

  const activeEvacZone = activeEvacZones.find(
    (activeZone) => activeZone.evacZone.uidV2 === feature.properties.uid_v2,
  );

  const poiDialogData: EvacZonePoi = {
    id: Number(feature.properties.uid_v2),
    uid: feature.properties.uid_v2,
    displayName: feature.properties.display_name,
    status: feature.properties.status || undefined,
    dateCreated: activeEvacZone?.dateCreated || feature.properties.date_created,
    dateModified:
      activeEvacZone?.dateModified || feature.properties.date_modified,
    source: feature.properties.source_attribution,
    style: feature.properties.evac_zone_style || EvacZoneStyles.default,
  };

  setSelectedPoi({
    type: 'activeEvacZone',
    id: poiDialogData.uid,
    metadata: poiDialogData,
  });
};

const handleClickEvent = (
  features: MapboxFeature<EvacZonesProperties>[],
  setSelectedPoi: (poi: Poi) => void,
  activeEvacZones: GeoEventEvacZoneStatus[],
): void => {
  for (const feature of features) {
    handleSingleClickFeature(feature, setSelectedPoi, activeEvacZones);
  }
};

/*
 * This layer is designed to show the zones with statuses globally on the map and/or
 * the active/inactive zones associated with a selected geo event's region(s)
 *
 * It handles the styling by grouping like colors/patterns within the possible groupings of style + status
 * and then filtering on the combinations of UIDs within those color/pattern groupings.
 */
const StructuredEvacuationsViewLayer = (): JSX.Element => {
  const { selectedPoi, setSelectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();
  const selectedEvacZoneUId = getSelectedEvacZoneUId(selectedPoi, true);
  const { appVersion } = useAppInfo();
  const activeEvacZones = useActiveEvacZonesQuery();

  const { geoEvent: selectedGeoEvent } = useGeoEventQuery<
    Wildfire | Location
  >();

  const clickCallback = useCallback(
    (features: MapboxFeature<EvacZonesProperties>[]) =>
      handleClickEvent(features, setSelectedPoi, activeEvacZones),
    [setSelectedPoi, activeEvacZones],
  );

  useMapLayerEvents({
    layerId: EVAC_ZONES_VIEW_FILL_LAYER_ID,
    onClick: clickCallback,
  });

  const selectedRegionIds = useMemo(() => {
    if (
      selectedGeoEvent?.geoEventType === 'wildfire' &&
      (selectedGeoEvent?.evacZoneStatuses.length ||
        selectedGeoEvent?.data.temporarilyDisplayEvacZones)
    ) {
      return selectedGeoEvent.regions.map((region) => region.id);
    }
    return [];
  }, [
    selectedGeoEvent?.data,
    selectedGeoEvent?.evacZoneStatuses.length,
    selectedGeoEvent?.geoEventType,
    selectedGeoEvent?.regions,
  ]);

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl(EVAC_ZONES_SOURCE_LAYER_NAME, {
        app_version: appVersion,
      })}
      visible
      promoteId={FEATURE_UID_V2_FIELD_NAME}
      // Do not unset `volatile` ever for this layer.
      volatile
      id={VIEW_SOURCE_ID}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={EVAC_ZONES_VIEW_FILL_LAYER_ID}
        {...getFillStyle({ regionIds: selectedRegionIds })}
      />
      <Layer id={EVAC_ZONES_VIEW_PATTERN_LAYER_ID} {...getPatternStyle()} />
      <Layer
        id={EVAC_ZONES_VIEW_STROKE_LAYER_ID}
        {...getStrokeStyle({
          isDarkBaseLayer,
          regionIds: selectedRegionIds,
        })}
      />
      <Layer
        beforeId={BEFORE_LAYER_ID}
        {...getSelectedZonePatternStyle(selectedEvacZoneUId)}
      />
      <Layer {...getSelectedZoneStrokeStyle(selectedEvacZoneUId)} />
      <Layer
        id={EVAC_ZONES_VIEW_LABEL_LAYER_ID}
        {...getLabelLayerStyle(selectedRegionIds)}
      />
    </VisibilityToggledSource>
  );
};

export default StructuredEvacuationsViewLayer;
